import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Trophy, Star, ChevronRight, PartyPopper, Swords } from 'lucide-react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { cn } from '../lib/utils';
import { saveLocalFirst } from '../lib/save-local-first';

interface TournamentCelebrationProps {
  game: any;
  result: {
    outcome: 'W' | 'L' | 'T';
    myScore: number;
    opponentScore: number;
  };
  teamId: string;
  teamName?: string;
  onClose: () => void;
}

export const TournamentCelebration = ({ game, result, teamId, teamName, onClose }: TournamentCelebrationProps) => {
  const [isCreatingNext, setIsCreatingNext] = useState(false);
  const isWin = result.outcome === 'W';
  const isChampionship = game.isChampionship;

  useEffect(() => {
    // Auto-create next bracket game if it's a win and NOT the championship
    const createNextGame = async () => {
      if (isWin && !isChampionship) {
        setIsCreatingNext(true);
        try {
          // saveLocalFirst so Continue doesn't stay disabled with no signal.
          await saveLocalFirst(addDoc(collection(db, `teams/${teamId}/games`), {
            tournamentId: game.tournamentId,
            tournamentPhase: 'bracket',
            tournamentGameNumber: (game.tournamentGameNumber || 0) + 1,
            opponent: 'TBD',
            location: game.location,
            date: new Date(new Date(game.date).getTime() + 2 * 60 * 60 * 1000).toISOString(), // +2 hours
            status: 'scheduled',
            isHome: true,
            createdAt: new Date().toISOString()
          }), 'next bracket game');
        } catch (error) {
          console.error("Failed to auto-create next bracket game", error);
        } finally {
          setIsCreatingNext(false);
        }
      }
    };

    createNextGame();
  }, [isWin, isChampionship, game, teamId]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-surface flex flex-col items-center justify-center p-6 text-center"
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 12 }}
        className="relative"
      >
        {isWin ? (
          <div className="relative">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <Trophy className={cn(
                "w-32 h-32 mx-auto mb-8",
                isChampionship ? "text-gold-ink" : "text-accent-ink"
              )} />
            </motion.div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, x: 0, y: 0 }}
                  animate={{ 
                    scale: [0, 1, 0],
                    x: Math.cos(i * 30 * Math.PI / 180) * 150,
                    y: Math.sin(i * 30 * Math.PI / 180) * 150
                  }}
                  transition={{ repeat: Infinity, duration: 1.5, delay: i * 0.1 }}
                >
                  <Star className="w-4 h-4 text-gold-ink fill-current" />
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <Swords className="w-32 h-32 text-ink-2 mx-auto mb-8" />
        )}
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="space-y-4 max-w-sm"
      >
        <h2 className="text-4xl font-black italic uppercase tracking-tighter">
          {isWin ? (
            isChampionship ? "Championship Secured!" : "Bracket Advance!"
          ) : (
            "Tough Battle"
          )}
        </h2>
        
        <p className="text-ink-2 font-semibold uppercase tracking-widest text-xs">
          {isWin ? (
            isChampionship 
              ? "The dugout is pure chaos. You just coached them to a title. Take it in."
              : "Survive and advance. The job isn't finished, but that was a statement win."
          ) : (
            "Bracket play is heartless. They left it all on the field. Keep their heads up."
          )}
        </p>

        <div className="py-8">
          <div className="inline-flex items-center space-x-4 bg-canvas px-8 py-4 rounded-3xl border border-hairline">
            <div className="text-right">
              <span className="block text-[10px] font-black text-ink-2 uppercase truncate max-w-[80px]">{teamName || 'Us'}</span>
              <span className="text-3xl font-black italic">{result.myScore}</span>
            </div>
            <div className="h-8 w-px bg-raised" />
            <div className="text-left">
              <span className="block text-[10px] font-black text-ink-2 uppercase truncate max-w-[80px]">{game.opponent || 'Opp'}</span>
              <span className="text-3xl font-black italic">{result.opponentScore}</span>
            </div>
          </div>
        </div>

        {isWin && !isChampionship && (
          <div className="bg-canvas border border-hairline p-4 rounded-2xl mb-6">
            <p className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest flex items-center justify-center space-x-2">
              <PartyPopper className="w-3 h-3" />
              <span>Next bracket game auto-created</span>
            </p>
          </div>
        )}

        <button
          onClick={onClose}
          disabled={isCreatingNext}
          className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic flex items-center justify-center space-x-2 hover:scale-105 transition-transform"
        >
          <span>{isWin && !isChampionship ? "Review Next Game" : "Back to Tournament"}</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </motion.div>
    </motion.div>
  );
};
